import { useEffect, useState } from "react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { WarningCircle } from "@phosphor-icons/react";
import { EmailEditor } from "./EmailEditor";
import { unknownVars, isBlank, normName } from "./render";

// Création / modification d'un template depuis la liste des templates (onglet Campagnes).
export function TemplateFormDialog({ open, onClose, template, existing = [], onSaved }) {
  const [f, setF] = useState({ name: "", subject: "", body_html: "" });
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (open) setF(template ? { name: template.name, subject: template.subject, body_html: template.body_html } : { name: "", subject: "", body_html: "<p>Bonjour {{prenom}},</p><p></p><p>Bien cordialement,</p>" });
  }, [open, template]);
  const unknown = unknownVars(f.subject, f.body_html);
  const nameTaken = (n) => existing.some((x) => normName(x.name) === normName(n) && x.id !== template?.id);
  const invalid = !f.name.trim() || !f.subject.trim() || isBlank(f.body_html) || unknown.length > 0;
  const run = async (asNew) => {
    if (asNew && (nameTaken(f.name) || (template && normName(f.name) === normName(template.name)))) { toast.error("Donnez un nom différent pour enregistrer un nouveau template."); return; }
    setBusy(true);
    try {
      const { data } = asNew ? await api.post("/admin/leads/templates", f)
        : await api.put(`/admin/leads/templates/${template.id}`, { ...f, expected_version: template.version });
      toast.success(asNew ? `Template « ${data.name} » créé` : `Template « ${data.name} » mis à jour (version ${data.version})`);
      onSaved?.(data); onClose();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); } finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="bg-surface border-border w-[95vw] max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="lead-template-form">
        <DialogHeader>
          <DialogTitle>{template ? `Modifier « ${template.name} »` : "Nouveau template"}</DialogTitle>
          <DialogDescription>{template ? `Version ${template.version}. Choisissez explicitement de remplacer ce template ou d'en créer un nouveau.` : "Ce template sera proposé lors de la préparation des prochaines campagnes."}</DialogDescription>
        </DialogHeader>
        <Input value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} maxLength={80} placeholder="Nom du template" className="bg-surface-2 border-border" data-testid="lead-template-form-name" />
        {nameTaken(f.name) && <p className="text-[11px] text-amber-500">Un autre template porte déjà ce nom.</p>}
        <Input value={f.subject} onChange={(e) => setF({ ...f, subject: e.target.value })} maxLength={200} placeholder="Objet de l'email" className="bg-surface-2 border-border" data-testid="lead-template-form-subject" />
        <EmailEditor value={f.body_html} onChange={(v) => setF((x) => ({ ...x, body_html: v }))} testid="lead-template-form-body" />
        {unknown.length > 0 && <p className="text-xs text-destructive flex gap-1"><WarningCircle size={14} className="shrink-0" /> Variables inconnues : {unknown.map((v) => `{{${v}}}`).join(", ")}</p>}
        <div className="flex flex-wrap gap-2">
          {template && <button onClick={() => run(false)} disabled={busy || invalid || nameTaken(f.name)} className="bg-accent text-black font-semibold rounded-md px-4 py-2 text-sm disabled:opacity-50" data-testid="lead-template-form-replace">Remplacer « {template.name} » (v{template.version} → v{template.version + 1})</button>}
          <button onClick={() => run(true)} disabled={busy || invalid || nameTaken(f.name)} data-testid="lead-template-form-create"
            className={`${template ? "border border-border text-fg" : "bg-accent text-black font-semibold"} rounded-md px-4 py-2 text-sm disabled:opacity-50`}>
            {template ? "Enregistrer comme nouveau template" : "Créer le template"}
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
